import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, Sparkles, User, MessageCircle, ExternalLink, CheckCircle2, CreditCard } from 'lucide-react';
import { FlutterWaveButton } from 'flutterwave-react-v3';
import api from '../services/api';

const API_URL = import.meta.env.VITE_API_URL || '';

const msgVariant = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: 'spring', stiffness: 200, damping: 20 } },
  exit: { opacity: 0, y: -10, scale: 0.95 },
};

const BotMsg = ({ children }) => (
  <motion.div variants={msgVariant} initial="hidden" animate="visible" exit="exit" className="flex gap-3 max-w-[85%]">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0 mt-1 shadow-lg shadow-primary-500/20">
      <Sparkles size={14} className="text-white" />
    </div>
    <div className="bg-gray-800/80 border border-gray-700/50 rounded-2xl rounded-tl-sm px-5 py-3 text-gray-200 leading-relaxed whitespace-pre-line">
      {children}
    </div>
  </motion.div>
);

const UserMsg = ({ children }) => (
  <motion.div variants={msgVariant} initial="hidden" animate="visible" exit="exit" className="flex gap-3 max-w-[85%] ml-auto flex-row-reverse">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent-500 to-pink-500 flex items-center justify-center flex-shrink-0 mt-1 shadow-lg shadow-accent-500/20">
      <User size={14} className="text-white" />
    </div>
    <div className="bg-primary-500/20 border border-primary-500/30 rounded-2xl rounded-tr-sm px-5 py-3 text-white">
      {children}
    </div>
  </motion.div>
);

const TypingIndicator = () => (
  <motion.div variants={msgVariant} initial="hidden" animate="visible" exit="exit" className="flex gap-3 max-w-[85%]">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center flex-shrink-0 mt-1">
      <Sparkles size={14} className="text-white" />
    </div>
    <div className="bg-gray-800/80 border border-gray-700/50 rounded-2xl rounded-tl-sm px-5 py-4 flex gap-1.5">
      {[0, 1, 2].map((i) => (
        <motion.div key={i} animate={{ y: [0, -6, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
          className="w-2 h-2 bg-gray-400 rounded-full" />
      ))}
    </div>
  </motion.div>
);

const QuickReply = ({ text, onClick, color = 'primary' }) => (
  <motion.button initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}
    onClick={onClick}
    className={`px-5 py-2.5 rounded-xl border font-medium transition-all text-sm ${
      color === 'red'
        ? 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20'
        : 'bg-primary-500/10 border-primary-500/30 text-primary-300 hover:bg-primary-500/20'
    }`}>
    {text}
  </motion.button>
);

const CreateRequest = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [paymentData, setPaymentData] = useState(null);
  const [contactLink, setContactLink] = useState('');
  const [emailVal, setEmailVal] = useState('');
  const [country, setCountry] = useState('');
  const [flutterwaveKey, setFlutterwaveKey] = useState('');
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const eventSourceRef = useRef(null);

  const addBot = (text) => setMessages(p => [...p, { type: 'bot', text, id: Date.now() + Math.random() }]);
  const addUser = (text) => setMessages(p => [...p, { type: 'user', text, id: Date.now() + Math.random() }]);

  useEffect(() => {
    api.get('/payments/flutterwave-key').then(res => {
      if (res.data.publicKey) setFlutterwaveKey(res.data.publicKey);
    }).catch(() => {});

    setTimeout(() => {
      addBot(
        `Welcome to Advance Advert Tv Digital!\n\n` +
        `We create foreign TikTok accounts for UK, USA, Canada, Germany, and more.\n\n` +
        `A foreign account helps you reach a global audience, get higher engagement, and unlock monetization.\n\n` +
        `Price: ₦5,000 (one-time)\n\n` +
        `Would you like to proceed?`
      );
      setStep(1);
    }, 500);

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
  }, []);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  // SSE listener for payment confirmation
  const joinPaymentRoom = async (txRef) => {
    const es = new EventSource(`${API_URL}/api/payments/stream/${txRef}`);
    eventSourceRef.current = es;

    es.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.status === 'success') {
          es.close();
          eventSourceRef.current = null;

          let cfg = null;
          try { const cr = await api.get('/store-config/public'); cfg = cr.data.config; } catch (_) {}
          const link = cfg?.contactLink || cfg?.whatsappLink || '';
          setContactLink(link);

          addBot(
            `Payment received! Your order is confirmed.\n\n` +
            (link ? `Kindly click the link below to get connected to a service agent who will help set up your account:` : `Your order is being processed. We'll reach out to you soon.`)
          );
          setStep(11);
        }
      } catch (_) {}
    };

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
    };
  };

  // When Flutterwave modal closes (whether paid or not)
  const onFlutterwaveClose = () => {
    // Don't remove SSE — let it keep listening for webhook
  };

  const handleProceed = () => {
    addUser('Proceed');
    setTimeout(() => {
      addBot(`Great! Let's get started.\n\nPlease enter your email address (the one never used on TikTok):`);
      setStep(2);
    }, 600);
  };

  const handleNotNow = () => {
    addUser('Not now');
    setTimeout(() => {
      addBot(
        `No worries at all! We're always here whenever you're ready.\n\n` +
        `If you'd like to stay updated on new offers and tips, drop your email below to subscribe to our newsletter!`
      );
      setStep(9);
    }, 600);
  };

  const handleCancel = () => {
    setStep(0);
  };

  const handleSubscribe = () => {
    if (!input.trim() || !input.includes('@')) {
      setTimeout(() => addBot('Please enter a valid email address.'), 300);
      return;
    }
    const email = input.trim();
    addUser(email);
    setInput('');
    setLoading(true);

    api.post('/newsletter/subscribe', { email })
      .then(() => {
        addBot(`Thanks for subscribing! We'll keep you updated with exclusive tips and offers.\n\nFeel free to come back anytime. Have a great day!`);
        setStep(10);
      })
      .catch(() => {
        addBot(`Thanks for your interest! We'll keep you posted.\n\nFeel free to come back anytime. Have a great day!`);
        setStep(10);
      })
      .finally(() => setLoading(false));
  };

  const handleEmailInput = () => {
    if (!input.trim() || !input.includes('@')) {
      setTimeout(() => addBot('Please enter a valid email address.'), 300);
      return;
    }
    setEmailVal(input.trim());
    addUser(input.trim());
    setInput('');
    setTimeout(() => {
      addBot(`Got it! Which country would you like your TikTok account from?\n\nUK, USA, Canada, Germany, etc.`);
      setStep(3);
    }, 400);
  };

  const handleCountryInput = () => {
    if (!input.trim()) {
      setTimeout(() => addBot('Please enter a country.'), 300);
      return;
    }
    setCountry(input.trim());
    addUser(input.trim());
    setInput('');
    setTimeout(() => {
      addBot(`Almost done! Enter a preferred username (or type "skip" to use a random one):`);
      setStep(4);
    }, 400);
  };

  const handleSubmitDetails = () => {
    const rawUsername = input.trim();
    const username = rawUsername.toLowerCase() === 'skip' || !rawUsername ? undefined : rawUsername;
    addUser(rawUsername || 'Skip');
    setInput('');
    setLoading(true);

    api.post('/requests', {
      email: emailVal,
      desiredCountry: country,
      username,
      package: 'vip',
      fullName: username || '',
      sessionId: Math.random().toString(36).substring(2, 15),
    })
      .then((res) => {
        const requestId = res.data.request._id;
        return api.post('/payments/initialize', { requestId, email: emailVal });
      })
      .then((res) => {
        const { txRef, amount, email, name } = res.data;
        if (!txRef) throw new Error('No tx_ref returned');
        setPaymentData({ txRef, amount, email, name });

        // Open SSE connection to listen for webhook confirmation
        joinPaymentRoom(txRef);

        addBot(
          `Your order has been created!\n\n` +
          `Country: ${country}\n` +
          `Email: ${emailVal}\n` +
          `Username: ${username || 'Not specified'}\n\n` +
          `Click "Pay Now" below to complete payment.`
        );
        setStep(8);
        setLoading(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.error || err.response?.data?.message || 'Something went wrong. Please try again.';
        addBot(msg);
        setStep(2);
        setLoading(false);
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (step === 2) handleEmailInput();
      else if (step === 3) handleCountryInput();
      else if (step === 4) handleSubmitDetails();
      else if (step === 9) handleSubscribe();
    }
  };

  const showOptions1 = step === 1;
  const showPayButton = step === 8 && paymentData && flutterwaveKey;
  const showInput = step === 2 || step === 3 || step === 4 || step === 9;

  return (
    <div className="min-h-screen pt-20 pb-4 px-4 flex flex-col bg-gray-950">
      <div className="max-w-lg mx-auto w-full flex-1 flex flex-col">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-500/10 border border-primary-500/20 mb-3">
            <MessageCircle size={14} className="text-primary-400" />
            <span className="text-sm text-primary-300">Advance Advert Tv Digital Assistant</span>
          </div>
          <h1 className="text-2xl font-bold text-white">Get Your Foreign Account</h1>
        </motion.div>

        <div ref={chatRef} className="flex-1 overflow-y-auto space-y-4 pb-4 mb-4 min-h-[400px] max-h-[60vh]" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          <AnimatePresence>
            {messages.map((msg) =>
              msg.type === 'bot'
                ? <BotMsg key={msg.id}>{msg.text}</BotMsg>
                : <UserMsg key={msg.id}>{msg.text}</UserMsg>
            )}
          </AnimatePresence>
          {loading && <TypingIndicator />}
        </div>

        <AnimatePresence>
          {showOptions1 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex gap-2 mb-4 flex-wrap items-center">
              <QuickReply text="Proceed" onClick={handleProceed} />
              <QuickReply text="Not now" onClick={handleNotNow} color="red" />
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                onClick={handleCancel}
                className="w-8 h-8 rounded-full bg-gray-800 border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 flex items-center justify-center text-xs transition-all">
                ✕
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showPayButton && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-4">
              <FlutterWaveButton
                amount={paymentData.amount}
                currency="NGN"
                tx_ref={paymentData.txRef}
                payment_options="card,banktransfer,ussd"
                customer={{
                  email: paymentData.email,
                  name: paymentData.name || paymentData.email,
                }}
                customizations={{
                  title: 'Advance Advert Tv Digital',
                  description: 'Foreign TikTok Account Purchase',
                }}
                public_key={flutterwaveKey}
                callback={() => {}}
                onClose={onFlutterwaveClose}
                text={<span className="flex items-center justify-center gap-2"><CreditCard size={20} /> Pay Now — ₦5,000</span>}
                className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-lg shadow-lg shadow-green-500/25 relative overflow-hidden hover:shadow-xl hover:shadow-green-500/30 transition-all"
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {step === 11 && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-4 space-y-3">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}
                className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto">
                <CheckCircle2 size={32} className="text-green-400" />
              </motion.div>
              {contactLink && (
                <motion.a href={contactLink} target="_blank" rel="noopener noreferrer"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(34,197,94,0.3)' }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-green-500/25 relative overflow-hidden"
                >
                  <ExternalLink size={20} />
                  <span>Chat with Service Agent</span>
                </motion.a>
              )}
              <button onClick={() => navigate('/')}
                className="w-full py-3 rounded-xl border border-gray-700 text-gray-400 text-sm hover:bg-gray-800 transition-all">
                Back to Home
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {step === 10 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-4">
            <p className="text-gray-500 text-sm">Thanks for chatting with us!</p>
            <button onClick={() => navigate('/')} className="mt-3 text-primary-400 text-sm hover:text-primary-300">Back to Home</button>
          </motion.div>
        )}

        {showInput && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex gap-2">
            <input ref={inputRef} type={step === 9 ? 'email' : 'text'} value={input}
              onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder={step === 9 ? 'Enter your email for updates...' : 'Type your response...'}
              className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-xl px-5 py-3.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-all placeholder-gray-500" />
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
              onClick={step === 9 ? handleSubscribe : step === 2 ? handleEmailInput : step === 3 ? handleCountryInput : handleSubmitDetails}
              disabled={!input.trim() || loading}
              className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center text-white shadow-lg shadow-primary-500/25 disabled:opacity-50 transition-all">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default CreateRequest;
